import React, { useContext, useEffect, useState } from 'react';
import { getItemDetails, getItemsList } from 'services/dumpert';
import {
  PreferenceContext,
  RequestContext,
  RouterContext,
  selectedIdParam,
  UserContext,
} from '../context';
import { pushToDataLayer } from 'services/datalayer';
import { useDataLoader, useDidomiConsent } from '../common/hooks';
import { getItemsListDefaultParams } from '../loaders';
import { removeEmpty } from '../common/o';
import { NotFound } from '../pages/NotFound';
import { notifyError } from '../notifications';
import { AdsContext } from '../ads';

export const ItemContext = React.createContext({});
ItemContext.displayName = 'Item';

const sortItemsByViewed = (items) => {
  try {
    const viewed = (window.sessionStorage.getItem('viewedItems') || '').split(',');
    return items.sort((x, y) =>
      !viewed.some((z) => z === y.id) ? 1 : !viewed.some((z) => z === x.id) ? -1 : -1,
    );
  } catch (e) {
    return items;
  }
};

const checkValidItemId = (id) => /^\d+_[a-z0-9]{7,8}$/.test(id);
const invalidItemIdError = new Error('invalid item id');

export const ItemContextProvider = ({ overWriteItemId, children }) => {
  const { setStatus } = useContext(RequestContext);
  const { isPlaylistItem, search, isModalActive, isDetailPage, routeMatch } =
    useContext(RouterContext);
  const currentItemId = isModalActive
    ? new URLSearchParams(search).get(selectedIdParam)
    : overWriteItemId || routeMatch.props.match.params.itemId;

  const { user } = useContext(UserContext);

  const { dispatchKachingConfig } = useContext(AdsContext);

  const [itemUrl, setItemUrl] = useState('');

  const didomiConsent = useDidomiConsent();

  const {
    preference: { nsfwActive },
  } = useContext(PreferenceContext);
  const showNsfwItems = nsfwActive ? { nsfwActive } : {};

  const loadItemDetails = useDataLoader(getItemDetails);
  const loadItemList = useDataLoader(getItemsList);

  const hasValidItemId = checkValidItemId(currentItemId) ? currentItemId : false;

  const itemLoad =
    (isPlaylistItem || isDetailPage) && hasValidItemId
      ? loadItemDetails({ itemId: currentItemId })
      : { error: invalidItemIdError };

  if ((itemLoad.error || itemLoad.result.status === 204) && setStatus) {
    setStatus(404);
  }

  const loadAdditionalInformation =
    hasValidItemId && !(itemLoad.error || itemLoad.result.status === 204);

  const listLoad = loadAdditionalInformation
    ? loadItemList(
        removeEmpty({
          ...getItemsListDefaultParams,
          list: 'related',
          itemId: currentItemId,
          ...showNsfwItems,
        }),
      )
    : { error: invalidItemIdError };

  const item =
    itemLoad && itemLoad.result && loadAdditionalInformation ? itemLoad.result.data.items[0] : null;

  const relatedItems =
    listLoad && listLoad.result
      ? sortItemsByViewed(
          listLoad.result.data.items

            .filter(
              ({ media, nsfw } = {}) =>
                !nsfw &&
                media &&
                media.length === 1 &&
                media[0].mediatype === 'VIDEO' &&
                media[0].variants &&
                media[0].variants.length > 0 &&
                media[0].variants[0].version !== 'embed',
            )
            .slice(0, 9)
            .map((i) => ({
              id: i.id,
              image: i.stills ? i.stills['still-medium'] : i.still,
              link: `/item/${i.id}`,
              extra: false,
              title: i.title,
            })),
        )
      : [];

  useEffect(() => {
    const { error } = itemLoad || {};
    if (error && error !== invalidItemIdError) {
      notifyError(error);
    }
  }, [itemLoad && itemLoad.error]);

  useEffect(() => {
    const { error } = itemLoad || {};
    if (error && error !== invalidItemIdError) {
      notifyError(error);
    }
  }, [listLoad && listLoad.error]);

  useEffect(() => {
    if (currentItemId !== undefined && item) {
      setItemUrl(`${window.location.origin}/item/${item.id}`);
      dispatchKachingConfig(
        isPlaylistItem ? { type: 'playlist', payload: item } : { type: 'item', payload: item },
      );
    }
  }, [JSON.stringify(item)]);

  useEffect(() => {
    if (currentItemId !== undefined && item && didomiConsent && didomiConsent.hasConsent) {
      pushToDataLayer(item, user !== null, true, didomiConsent.consentStatusForAll);
    }
  }, [didomiConsent]);

  return (
    <ItemContext.Provider
      value={{
        item,
        itemUrl,
        currentItemId,
        relatedItems,
      }}
    >
      {isDetailPage &&
        (!itemLoad || itemLoad.error || (itemLoad && itemLoad.result.status === 204)) && (
          <NotFound />
        )}
      {loadAdditionalInformation && <>{children}</>}
    </ItemContext.Provider>
  );
};
